/* @flow */

import * as React from 'react';
import { useCallback, useRef } from 'react';

import type { ModalState } from '../../../redux/modal/reducers';

import ButtonFX from '../../buttons/ButtonFX';
import { WidthKind } from '../../buttons/types';
import Modal, { ModalView } from '../modal';
import './ConfirmationModal.css';

const { Fragment } = React;

export enum ConfirmationModalResult {
  Button1,
  Button2,
  Button3,
  Cancel,
}

export type CONFIRMATION_DATA_MODAL_TYPE = {
  button1Title: string,
  button2Title: string,
  button3Title?: string,
  header?: string,
  question: string,
  title?: string,
};

const ConfirmationModal = ({ ariaLabel, confirmationData, modalId }: ModalState): React.Node => {
  const modalRef = useRef<ModalView | null>(null);

  const setModalRef = useCallback((instance: ModalView | null) => {
    modalRef.current = instance;
  }, []);

  const close = useCallback((result: ConfirmationModalResult) => {
    modalRef.current?.close(result);
  }, []);

  const handleButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, result: ConfirmationModalResult) => {
      close(result);
    },
    [close],
  );

  const renderQuestion = useCallback((): React.Node => {
    if (!confirmationData) {
      return null;
    }

    const { question } = confirmationData;

    return (
      <div className='question'>
        {question.split('\n').map((line) => (
          <Fragment key={line}>
            {line}
            <br />
          </Fragment>
        ))}
      </div>
    );
  }, [confirmationData]);

  const renderButtons = useCallback((): React.Node => {
    if (!confirmationData) {
      return null;
    }

    const { button1Title, button2Title, button3Title } = confirmationData;

    return (
      <div className='buttons'>
        {button3Title ? (
          <ButtonFX data={ConfirmationModalResult.Button3} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
            {button3Title}
          </ButtonFX>
        ) : null}
        <ButtonFX data={ConfirmationModalResult.Button2} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
          {button2Title}
        </ButtonFX>
        <ButtonFX data={ConfirmationModalResult.Button1} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
          {button1Title}
        </ButtonFX>
      </div>
    );
  }, [confirmationData]);

  if (!confirmationData) {
    return null;
  }

  const { header, title } = confirmationData;

  return (
    <Modal ariaLabel={ariaLabel} className='confirmation' header={header} innerRef={setModalRef} isAlert modalId={modalId} title={title}>
      {renderQuestion()}
      {renderButtons()}
    </Modal>
  );
};

export default ConfirmationModal;
